/**
 * LH Personalization — lh-personalization.js
 *
 * Tracks channel signals (Attentive, Google Ads, Klaviyo) in a persistent
 * 90-day cookie (lh_channel) and browse history in localStorage (lh_rv_products).
 * Exposes a merge-and-rank function used by homepage + collection reorder scripts.
 *
 * Signal priority (highest → lowest):
 *   1. Attentive (SMS — highest purchase intent)
 *   2. Klaviyo (email)
 *   3. Google Ads (paid search)
 *   4. On-site browse history (recently viewed)
 */

(function (global) {
  'use strict';

  // ---------------------------------------------------------------------------
  // Cookie helpers
  // ---------------------------------------------------------------------------

  function readCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, days) {
    try {
      var expires = '';
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + d.toUTCString();
      }
      document.cookie =
        name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Signal store  (lh_channel cookie)
  // ---------------------------------------------------------------------------

  var COOKIE_NAME    = 'lh_channel';
  var COOKIE_DAYS    = 90;
  var MAX_SIGNALS    = 10;

  /**
   * Source priority weights — higher = surfaces matching products first.
   */
  var SOURCE_WEIGHT = {
    attentive : 4,
    klaviyo   : 3,
    google    : 2,
    browse    : 1   // recently viewed fallback
  };

  function getSignals() {
    try {
      var raw = readCookie(COOKIE_NAME);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveSignals(signals) {
    writeCookie(COOKIE_NAME, JSON.stringify(signals), COOKIE_DAYS);
  }

  /**
   * addSignal — called by captureUTM() when UTM params are found in the URL.
   * Deduplicates by source+collection, keeps newest, trims to MAX_SIGNALS.
   */
  function addSignal(source, medium, collection) {
    if (!source) return;
    var signals = getSignals();

    // Remove any existing entry for same source + collection
    signals = signals.filter(function (s) {
      return !(s.source === source && s.collection === collection);
    });

    signals.unshift({
      source     : source,
      medium     : medium     || '',
      collection : collection || '',
      product    : '',         // filled in by tagSignalProduct() if visitor lands on a PDP
      ts         : Math.floor(Date.now() / 1000)
    });

    saveSignals(signals.slice(0, MAX_SIGNALS));
  }

  /**
   * tagSignalProduct — called from main-product--default.liquid when a visitor
   * lands on a product page via a known UTM channel.
   * Finds the most recent signal from that source and stamps the exact product handle on it.
   *
   * @param {string} source      — e.g. 'attentive', 'klaviyo', 'google'
   * @param {string} handle      — product.handle
   */
  function tagSignalProduct(source, handle) {
    if (!source || !handle) return;
    var signals = getSignals();
    for (var i = 0; i < signals.length; i++) {
      if (signals[i].source === source) {
        signals[i].product = handle;
        saveSignals(signals);
        return;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // UTM capture — runs on every page load via layout/theme.liquid
  // ---------------------------------------------------------------------------

  /**
   * UTM → source mapping.
   * Attentive:  utm_source=attentive  utm_medium=sms
   * Klaviyo:    utm_source=klaviyo    utm_medium=email
   * Google Ads: utm_source=google     utm_medium=cpc  (or paidsearch)
   *
   * utm_content should carry the collection slug, e.g. "engagement-rings"
   */
  function captureUTM() {
    try {
      var params     = new URLSearchParams(window.location.search);
      var source     = (params.get('utm_source')   || '').toLowerCase();
      var medium     = (params.get('utm_medium')   || '').toLowerCase();
      var collection = (params.get('utm_content')  || '').toLowerCase();  // collection slug

      // Only record known paid/owned channels — ignore organic/direct
      var knownSources = ['attentive', 'klaviyo', 'google'];
      if (source && knownSources.indexOf(source) !== -1) {
        addSignal(source, medium, collection);
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Merge-and-rank — combines channel signals + browse history
  // ---------------------------------------------------------------------------

  /**
   * getRankedHandles(currentCollection)
   *
   * Returns an ordered array of product handles to surface first.
   *
   * Logic:
   *   1. Build a score map for each product handle
   *   2. Channel signals add (SOURCE_WEIGHT * recency_decay) to handles
   *      whose stored collection matches the signal's collection
   *   3. Browse history adds SOURCE_WEIGHT.browse for all recently viewed
   *   4. Sort by score descending
   *
   * @param {string} [currentCollection] — optional filter: only boost products
   *   from this collection (pass collection.handle on collection pages)
   * @returns {string[]} ordered array of handles
   */
  function getRankedHandles(currentCollection) {
    try {
      var scores = {};

      // --- Channel signals ---
      var signals = getSignals();
      var now     = Math.floor(Date.now() / 1000);
      var DAY_S   = 86400;

      signals.forEach(function (sig) {
        if (!sig.collection) return;

        // Skip if we're on a collection page and signal doesn't match
        if (currentCollection && sig.collection !== currentCollection) return;

        var weight = SOURCE_WEIGHT[sig.source] || 1;
        var ageDays = (now - (sig.ts || now)) / DAY_S;
        // Recency decay: full weight today, halves every 30 days
        var decay = Math.pow(0.5, ageDays / 30);
        var signalScore = weight * decay;

        // Apply this signal's score to all RV products matching the collection
        var rvProducts = getRVProducts();
        rvProducts.forEach(function (p) {
          if (p.collection === sig.collection) {
            scores[p.handle] = (scores[p.handle] || 0) + signalScore;
          }
        });
      });

      // --- Browse history fallback ---
      var rvProducts = getRVProducts();
      // Build index map: handle → position in array (higher = more recently viewed)
      var rvIndex = {};
      rvProducts.forEach(function (p, idx) { rvIndex[p.handle] = idx; });

      rvProducts.forEach(function (p) {
        // Skip if on a collection page and product has a DIFFERENT (non-empty) collection stored
        // Products with no collection stored (direct URL visits) are always included
        if (currentCollection && p.collection && p.collection !== currentCollection) return;

        // Give every RV product a baseline browse score if not already scored
        if (!scores[p.handle]) {
          scores[p.handle] = SOURCE_WEIGHT.browse;
        }
      });

      // Sort handles by score descending; use recency (array index) as tiebreaker
      return Object.keys(scores).sort(function (a, b) {
        var diff = scores[b] - scores[a];
        if (diff !== 0) return diff;
        return (rvIndex[b] || 0) - (rvIndex[a] || 0); // higher idx = more recently viewed
      });

    } catch (e) {
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Recently viewed products helper
  // ---------------------------------------------------------------------------

  function getRVProducts() {
    try {
      var raw = localStorage.getItem('lh_rv_products');
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Collection page seeding — called when visitor arrives via channel UTM
  // ---------------------------------------------------------------------------

  /**
   * captureCollectionProducts
   *
   * Called on collection pages. If the visitor arrived via a known channel UTM
   * (Attentive, Klaviyo, Google Ads), seeds the top products from that collection
   * into lh_rv_products so the homepage featured section has products to inject.
   *
   * Products added this way are appended AFTER any organically viewed products
   * so real browse history always takes priority.
   *
   * Reads from window.__lh_collection_products — a JSON array rendered by
   * lh-main-collection-product-grid.liquid.
   */
  function captureCollectionProducts() {
    try {
      var params = new URLSearchParams(window.location.search);
      var source = (params.get('utm_source') || '').toLowerCase();
      var knownSources = ['attentive', 'klaviyo', 'google'];
      if (!source || knownSources.indexOf(source) === -1) return;

      var collectionProducts = window.__lh_collection_products;
      if (!collectionProducts || !collectionProducts.length) return;

      var stored = getRVProducts();
      var storedHandles = stored.map(function (p) { return p.handle; });

      // Append collection products that aren't already stored
      collectionProducts.forEach(function (p) {
        if (storedHandles.indexOf(p.handle) === -1) {
          stored.push(p);
        }
      });

      localStorage.setItem('lh_rv_products', JSON.stringify(stored.slice(0, 16)));
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // DOM reorder helpers — called directly by section scripts
  // ---------------------------------------------------------------------------

  /**
   * reorderSwiper(wrapper, currentCollection)
   * Used by featured-collection-grid.liquid
   * Moves matched slides to the front of the swiper-wrapper BEFORE Swiper inits.
   *
   * @param {Element} wrapper      — .swiper-wrapper element
   * @param {string}  [currentCollection]
   */
  function reorderSwiper(wrapper, currentCollection) {
    try {
      if (!wrapper) return;
      var ranked = getRankedHandles(currentCollection);
      if (!ranked.length) return;

      // Insert in reverse so first-ranked ends up at index 0
      for (var i = ranked.length - 1; i >= 0; i--) {
        var slide = wrapper.querySelector('[data-product-handle="' + ranked[i] + '"]');
        if (slide) wrapper.insertBefore(slide, wrapper.firstChild);
      }
    } catch (e) {}
  }

  /**
   * reorderGrid(container, currentCollection)
   * Used by main-collection-product-grid.liquid
   *
   * @param {Element} container    — .collection__products element
   * @param {string}  [currentCollection]
   */
  function reorderGrid(container, currentCollection) {
    try {
      if (!container) return;
      var ranked = getRankedHandles(currentCollection);
      if (!ranked.length) return;

      for (var i = ranked.length - 1; i >= 0; i--) {
        var el = container.querySelector('[data-product-handle="' + ranked[i] + '"]');
        if (el) container.insertBefore(el, container.firstChild);
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Card injection — builds lh-fp__card HTML from stored product data
  // ---------------------------------------------------------------------------

  function esc(str) {
    return String(str || '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  // ---------------------------------------------------------------------------
  // Hover-media back-fill — patches stale lh_rv_products entries that were
  // stored before image2/hover_video tracking was added.
  // Uses Shopify's /products/{handle}.js endpoint (images[] array).
  // ---------------------------------------------------------------------------

  /**
   * fetchProductImages — returns a Promise that resolves to the full images[]
   * array from Shopify's product JSON endpoint, or null if unavailable.
   */
  function fetchProductImages(handle) {
    if (!window.fetch) return Promise.resolve(null);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.images || !data.images.length) return null;
        return data.images; // full array of CDN URLs
      })
      .catch(function () { return null; });
  }

  /**
   * patchRVProduct — updates a single entry in lh_rv_products with new fields.
   */
  function patchRVProduct(handle, fields) {
    try {
      var stored = getRVProducts();
      var changed = false;
      for (var i = 0; i < stored.length; i++) {
        if (stored[i].handle === handle) {
          var keys = Object.keys(fields);
          for (var k = 0; k < keys.length; k++) {
            stored[i][keys[k]] = fields[keys[k]];
          }
          changed = true;
          break;
        }
      }
      if (changed) localStorage.setItem('lh_rv_products', JSON.stringify(stored));
    } catch (e) {}
  }

  /**
   * buildFPCard — renders a product card HTML string matching lh-featured-products CSS.
   * @param {Object} p — product object from lh_rv_products
   * @returns {string} HTML string
   */
  function buildFPCard(p) {
    var badge = '';
    if (p.badge) {
      var badgeClass = 'lh-fp__badge';
      if (p.badge === 'Sale')      badgeClass += ' lh-fp__badge--sale';
      if (p.badge === 'Sold Out')  badgeClass += ' lh-fp__badge--muted';
      badge = '<span class="' + badgeClass + '">' + esc(p.badge) + '</span>';
    }

    var wishlist = '';
    if (p.id && p.variant_id) {
      wishlist = '<button' +
        ' class="lh-fp__wish swym-button swym-add-to-wishlist product_' + esc(p.id) + '"' +
        ' type="button"' +
        ' aria-label="Add ' + esc(p.name) + ' to wishlist"' +
        ' data-with-epi="true"' +
        ' data-swaction="addToWishlist"' +
        ' data-product-id="' + esc(p.id) + '"' +
        ' data-variant-id="' + esc(p.variant_id) + '"' +
        ' data-product-url="' + esc(p.url) + '">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M11.926 18.874c4.609-4.015 8.176-7.694 8.176-10.661 0-2.151-1.577-3.713-3.75-3.713-1.898 0-3.477 1.393-4.426 2.989C10.977 5.893 9.398 4.5 7.5 4.5c-2.173 0-3.75 1.561-3.75 3.713 0 2.967 3.567 6.646 8.176 10.661z" stroke="#111111" stroke-width="1.3"/>' +
        '</svg></button>';
    }

    var hasHover = !!(p.hover_video || p.image2 || (p.images && p.images.length > 1));

    var hoverEl = '';
    if (p.hover_video) {
      hoverEl = '<video class="lh-fp__card-vid--hover" autoplay loop muted playsinline preload="metadata">' +
        '<source src="' + esc(p.hover_video) + '">' +
        '</video>';
    } else if (p.image2) {
      hoverEl = '<img class="lh-fp__card-img lh-fp__card-img--alt" src="' + esc(p.image2) + '" alt="' + esc(p.name) + '" width="800" loading="lazy">';
    }

    // Mobile swipe carousel — mirrors lh-card-swipe-slides.liquid.
    // Uses p.images (full list) when available, falls back to [image, image2].
    // lh-card-desktop-link on the static <a> hides it on touch devices so the
    // swipe takes over on mobile, matching how Liquid-rendered cards work.
    var swipeHtml = '';
    var slideImgs = (p.images && p.images.length > 1) ? p.images
                  : (p.image2 ? [p.image, p.image2] : null);
    if (slideImgs && slideImgs.length > 1) {
      var slidesHtml = '';
      var dotsHtml = '';
      for (var si = 0; si < slideImgs.length; si++) {
        var isFirst = si === 0;
        // First slide = product shot: contain+white to match desktop card style.
        // Subsequent slides = lifestyle/detail: default cover.
        var imgStyle = isFirst ? ' data-lh-contain="1"' : '';
        slidesHtml += '<div class="lh-card-swipe__slide"><img class="lh-card-swipe__img"' + imgStyle + ' src="' + esc(slideImgs[si]) + '" alt="' + esc(p.name) + '" loading="lazy"></div>';
        dotsHtml   += '<span class="lh-card-swipe__dot' + (isFirst ? ' lh-card-swipe__dot--active' : '') + '"></span>';
      }
      swipeHtml =
        '<div class="lh-card-swipe" data-href="' + esc(p.url) + '">' +
          '<div class="lh-card-swipe__overflow">' +
            '<div class="lh-card-swipe__track">' + slidesHtml + '</div>' +
          '</div>' +
          '<div class="lh-card-swipe__dots">' + dotsHtml + '</div>' +
        '</div>';
    }

    return '<div class="lh-fp__card lh-fp__fade lh-fp__card--personalized" data-product-handle="' + esc(p.handle) + '">' +
      '<div class="lh-fp__card-media' + (hasHover ? ' lh-fp__card-media--has-alt' : '') + '">' +
        '<a href="' + esc(p.url) + '" class="lh-fp__media-link' + (hasHover ? ' lh-card-desktop-link' : '') + '" aria-label="' + esc(p.name) + '">' +
          '<img class="lh-fp__card-img lh-fp__card-img--main" src="' + esc(p.image) + '" alt="' + esc(p.name) + '" width="800" loading="lazy">' +
          hoverEl +
        '</a>' +
        swipeHtml +
        badge +
        wishlist +
      '</div>' +
      '<a href="' + esc(p.url) + '" class="lh-fp__info-link" tabindex="-1" aria-hidden="true">' +
        '<div class="lh-fp__card-info">' +
          '<p class="lh-fp__card-name">' + esc(p.name) + '</p>' +
          (p.tags && p.tags.indexOf('hide-price') !== -1 ? '' : '<p class="lh-fp__card-price">' + esc(p.price) + '</p>') +
        '</div>' +
      '</a>' +
    '</div>';
  }

  /**
   * _scoreAndRank — shared scoring logic.
   * Returns an ordered array of { product, handle } for the top `count` products.
   */
  function _scoreAndRank(rvProducts, count) {
    var signals = getSignals();
    var now     = Math.floor(Date.now() / 1000);
    var DAY_S   = 86400;

    var scored = {};
    rvProducts.forEach(function (p, idx) {
      var score = SOURCE_WEIGHT.browse;

      signals.forEach(function (sig) {
        var weight  = SOURCE_WEIGHT[sig.source] || 1;
        var ageDays = (now - (sig.ts || now)) / DAY_S;
        var decay   = Math.pow(0.5, ageDays / 30);

        if (sig.product && sig.product === p.handle) {
          score = Math.max(score, weight * decay * 2);
        } else if (sig.collection && sig.collection === p.collection) {
          score = Math.max(score, weight * decay);
        }
      });

      scored[p.handle] = { score: score, product: p, idx: idx };
    });

    var ranked = Object.keys(scored).sort(function (a, b) {
      var diff = scored[b].score - scored[a].score;
      if (diff !== 0) return diff;
      return scored[b].idx - scored[a].idx;
    });

    var top = [];
    for (var i = 0; i < ranked.length && top.length < count; i++) {
      top.push(scored[ranked[i]].product);
    }
    return top;
  }

  /**
   * _insertCards — builds and prepends card HTML, hides duplicate block cards,
   * caps total visible cards at maxVisible, then triggers the reveal animation.
   *
   * @param {Element} grid
   * @param {Array}   products
   * @param {number}  maxVisible — hard cap on total visible cards (injected + block)
   */
  function _insertCards(grid, products, maxVisible) {
    products.forEach(function (p) {
      var existing = grid.querySelector('[data-product-handle="' + p.handle + '"]');
      if (existing) existing.style.display = 'none';
      grid.insertAdjacentHTML('afterbegin', buildFPCard(p));
    });

    // Cap total visible cards — hides surplus block cards from the end.
    // Runs here (post-injection) because injectPersonalizedCards is async and
    // the section script's own cap fires before injected cards land in the DOM.
    if (maxVisible) {
      var allVisible = Array.prototype.slice.call(
        grid.querySelectorAll('.lh-fp__card')
      ).filter(function (c) { return c.style.display !== 'none'; });
      for (var ci = maxVisible; ci < allVisible.length; ci++) {
        allVisible[ci].style.display = 'none';
      }
    }

    var injected = grid.querySelectorAll('.lh-fp__card--personalized');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    injected.forEach(function (card, idx) {
      if (reducedMotion) {
        card.style.setProperty('--lh-fp-delay', '0ms');
        card.classList.add('lh-fp--visible');
      } else {
        card.style.setProperty('--lh-fp-delay', (idx * 90) + 'ms');
        requestAnimationFrame(function () { card.classList.add('lh-fp--visible'); });
      }
    });

    // Wire up swipe carousels for any injected cards on touch devices
    if (typeof window.lhInitCardSwipe === 'function') {
      window.lhInitCardSwipe();
    }
  }

  /**
   * injectPersonalizedCards — scores RV products against channel signals,
   * builds card HTML for the top matches, and PREPENDS them into the grid.
   * Hand-picked block cards that match an injected handle are hidden to avoid duplication.
   *
   * Stale entries (stored before image2/hover_video tracking) are automatically
   * patched via /products/{handle}.js before cards are built.
   *
   * Scoring:
   *   - Exact product handle match in a signal  → signal weight × 2 (strongest intent)
   *   - Collection match in a signal            → signal weight × recency decay
   *   - Recently viewed with no signal match    → browse weight (1)
   *
   * @param {Element} grid   — .lh-fp__grid element
   * @param {number}  count  — max personalized cards to inject (default 4)
   */
  function injectPersonalizedCards(grid, count) {
    try {
      if (!grid) return;
      count = count || 4;

      var rvProducts = getRVProducts();
      if (!rvProducts.length) return;

      var top = _scoreAndRank(rvProducts, count);

      // Find products missing the full images array (stale localStorage entries)
      var needPatch = top.filter(function (p) { return !p.images; });

      if (!needPatch.length || !window.fetch) {
        // All products already have full image data (or fetch unavailable) — inject now
        _insertCards(grid, top, count);
        return;
      }

      // Fetch full image list for stale entries in parallel, then inject
      var fetches = needPatch.map(function (p) {
        return fetchProductImages(p.handle).then(function (imgs) {
          return { handle: p.handle, imgs: imgs };
        });
      });

      Promise.all(fetches).then(function (results) {
        results.forEach(function (r) {
          if (!r.imgs || !r.imgs.length) return;
          // Patch in-memory product object
          for (var i = 0; i < top.length; i++) {
            if (top[i].handle === r.handle) {
              top[i].images      = r.imgs;
              top[i].image2      = r.imgs[1] || '';
              top[i].hover_video = '';
              break;
            }
          }
          // Persist to localStorage so next load is instant
          patchRVProduct(r.handle, { images: r.imgs, image2: r.imgs[1] || '', hover_video: '' });
        });
        _insertCards(grid, top, count);
      }).catch(function () {
        // Fetch failed — inject without full image data rather than showing nothing
        _insertCards(grid, top, count);
      });

    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  global.LHPersonalization = {
    captureUTM               : captureUTM,
    captureCollectionProducts: captureCollectionProducts,
    addSignal                : addSignal,
    tagSignalProduct         : tagSignalProduct,
    getSignals               : getSignals,
    getRVProducts            : getRVProducts,
    getRankedHandles         : getRankedHandles,
    reorderSwiper            : reorderSwiper,
    reorderGrid              : reorderGrid,
    injectPersonalizedCards  : injectPersonalizedCards
  };

})(window);
